import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

export default function RegisterPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "authority" ? "authority" : "family";
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: initialRole,
    organization: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success(`Account created. Welcome, ${res.user.name}.`);
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="mx-auto flex max-w-xl flex-col px-6 py-16" data-testid="register-page">
      <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">{t("auth.register_title")}</h1>
      <p className="mt-2 font-body text-sm text-[#5C6873]">{t("auth.register_sub")}</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-6">
        <div>
          <Label className="font-body text-sm text-[#1A2024]">{t("auth.iam")}</Label>
          <RadioGroup
            value={form.role}
            onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
            className="mt-2 grid gap-2 sm:grid-cols-2"
          >
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-base ${
                form.role === "family" ? "border-[#2E3A46] bg-[#F8F9FA]" : "border-[#E2E8F0] bg-white"
              }`}
              data-testid="register-role-family"
            >
              <RadioGroupItem value="family" id="role-family" className="mt-1" />
              <div>
                <div className="font-heading text-sm font-semibold text-[#1A2024]">{t("auth.role.family")}</div>
                <div className="font-body text-xs text-[#5C6873]">{t("auth.role.family_sub")}</div>
              </div>
            </label>
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-base ${
                form.role === "authority" ? "border-[#2E3A46] bg-[#F8F9FA]" : "border-[#E2E8F0] bg-white"
              }`}
              data-testid="register-role-authority"
            >
              <RadioGroupItem value="authority" id="role-authority" className="mt-1" />
              <div>
                <div className="font-heading text-sm font-semibold text-[#1A2024]">{t("auth.role.authority")}</div>
                <div className="font-body text-xs text-[#5C6873]">{t("auth.role.authority_sub")}</div>
              </div>
            </label>
          </RadioGroup>
          {form.role === "authority" && (
            <div className="mt-3 rounded-md border border-[#C99B4B]/40 bg-[#C99B4B]/10 px-3 py-2 font-body text-xs text-[#8A6A2E]" data-testid="register-authority-notice">
              {t("auth.authority_notice")}
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="name" className="font-body text-sm text-[#1A2024]">Full name</Label>
            <Input id="name" required value={form.name} onChange={handle("name")} className="mt-1.5 h-11 border-[#E2E8F0] bg-white" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="org" className="font-body text-sm text-[#1A2024]">
              Organisation {form.role === "family" ? <span className="text-[#5C6873]">(optional)</span> : ""}
            </Label>
            <Input id="org" value={form.organization} onChange={handle("organization")} className="mt-1.5 h-11 border-[#E2E8F0] bg-white" data-testid="register-org-input" />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="font-body text-sm text-[#1A2024]">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={handle("email")} className="mt-1.5 h-11 border-[#E2E8F0] bg-white" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="font-body text-sm text-[#1A2024]">Password</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={handle("password")} className="mt-1.5 h-11 border-[#E2E8F0] bg-white" data-testid="register-password-input" />
          <p className="mt-1 font-body text-xs text-[#5C6873]">At least 6 characters.</p>
        </div>

        {error && (
          <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="register-error">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="h-11 w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]"
        >
          {loading ? t("auth.creating") : t("nav.create_account")}
        </Button>

        <p className="font-body text-sm text-[#5C6873]">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[#2E3A46] hover:underline" data-testid="register-to-login">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
